import { recoverStaleSurveySession } from '@chiroup/components/body/recoverStaleSurveySession';
import React, { useState } from 'react';
import { reportRequestSubmitError } from '../../services/reportError';
import requestService from '../../services/request.service';
import { SoftUploadPanel, UploadKind, UploadStatus } from '../portal/uploadPanels';

type Props = {
  kind: UploadKind;
  requestId: string;
  validationCode: string | number;
  language: 'en' | 'es';
  primaryColor: string;
  prMap?: { [key: string]: string };
  alreadyComplete?: boolean;
  onComplete: () => void;
};

/**
 * `/requests/:id` upload step (Photo ID or Insurance card). Wraps
 * SoftUploadPanel with the validationCode-keyed pre-sign + S3 upload
 * helpers from `request.service`.
 */
const StepUpload: React.FC<Props> = ({
  kind,
  requestId,
  validationCode,
  language,
  primaryColor,
  prMap,
  alreadyComplete,
  onComplete,
}) => {
  const [status, setStatus] = useState<UploadStatus>(alreadyComplete ? 'submitted' : 'ready');

  const handleUpload = async (side: 'front' | 'back', file: File) => {
    await requestService.uploadFile({
      type: kind,
      requestId,
      validationCode,
      file,
      side,
      prMap,
    });
  };

  const handleSkip = async () => {
    setStatus('submitting');

    try {
      await requestService.skipUpload({ type: kind, requestId, validationCode });
      setStatus('submitted');
    } catch (err) {
      if (recoverStaleSurveySession({ err, language })) return;

      reportRequestSubmitError(err, { step: `skipUpload:${kind}` });
      setStatus('error');
    }
  };

  const handleDone = () => setStatus('submitted');

  return (
    <SoftUploadPanel
      kind={kind}
      primaryColor={primaryColor}
      language={language}
      status={status}
      onUpload={handleUpload}
      onDone={handleDone}
      onSkip={handleSkip}
      onExit={onComplete}
      exitLabel={language === 'es' ? 'Continuar' : 'Continue'}
    />
  );
};

export default StepUpload;
